<template>
  <div v-if="!disabled">
    <FlotoFormItem
      v-if="field.inputType === 'text'"
      :key="field.key"
      v-model="item.value"
      :vid="field.key"
      auto-focus
      :validation-label="field.name"
      :placeholder="field.name"
      :rules="validationRules(field)"
      :disabled="disabled"
    />
    <FlotoFormItem
      v-else-if="field.inputType === 'textarea'"
      :key="field.key"
      v-model="item.value"
      :vid="field.key"
      auto-focus
      type="textarea"
      :validation-label="field.name"
      :placeholder="field.name"
      rules="description"
      :disabled="disabled"
    />
    <FlotoFormItem
      v-else
      :key="field.key"
      :vid="field.key"
      :validation-label="field.name"
      rules="required|nonzero"
    >
      <div
        v-if="field.inputType === 'number'"
        :class="{ flex: field.unit, 'w-full': field.unit }"
      >
        <MInputGroup v-if="field.unit" compact class="input-group-container">
          <div class="flex-1">
            <MInputNumber
              v-model="item.value"
              auto-focus
              :disabled="disabled"
              :precision="field.precision ? field.precision : 0"
              :rules="validationRules(field)"
            />
          </div>
          <div
            v-if="field.unit"
            class="px-2 bg-neutral-lightest flex items-center justify-center text-neutral border-solid border-neutral-lighter border-l-0 border-t border-b border-r rounded rounded-tl-none rounded-bl-none"
            style="display: inline-flex"
          >
            {{ $tc(field.unit) }}
          </div>
        </MInputGroup>
        <FlotoFormItem
          v-else
          v-model="item.value"
          type="number"
          :validation-label="field.name"
          :rules="validationRules(field)"
          :disabled="disabled"
        />
        <!-- <MInputNumber
                    v-else
                    v-model="item.value"
                    :disabled="disabled"
                    :precision="field.precision ? field.precision : 0"
                    :rules="validationRules(field)"
                  /> -->
      </div>
      <TimePicker
        v-if="field.inputType === 'timePicker'"
        v-model="item.value"
        :unit="field.unit ? field.unit : 'minutes'"
        :disabled="disabled"
      />
      <BytesPicker
        v-if="field.inputType === 'bytes'"
        v-model="item.value"
        auto-focus
        :unit="field.unit ? field.unit : 'gb'"
        :disabled="disabled"
      />
      <HertzPicker
        v-if="field.inputType === 'hertz'"
        v-model="item.value"
        auto-focus
        :unit="field.unit ? field.unit : 'ghz'"
        :disabled="disabled"
      />
      <EnumDropdownPicker
        v-if="field.inputType === 'boolean'"
        v-model="item.value"
        as-input
        :disabled="disabled"
        :unassigned-value="false"
        :options="
          field.contextOptionKey
            ? propertyComponentContext[field.contextOptionKey]
            : propertyComponentContext.booleanOptions
        "
      />
      <EnumDropdownPicker
        v-if="field.inputType === 'dropdown'"
        v-model="item.value"
        searchable
        as-input
        :disabled="disabled"
        :options="propertyComponentContext[field.contextOptionKey]"
      />
      <FlotoTagsPicker
        v-if="field.inputType === 'tags'"
        v-model="item.value"
        type="multiStringValue"
        :title="field.name"
        :auto-complete-search="false"
        :disabled="disabled"
      />
      <FlotoDatePicker
        v-if="field.inputType === 'dateTime' || field.inputType === 'date'"
        v-model="item.value"
        :show-time="field.inputType === 'dateTime'"
        as-input
        :disabled="disabled"
      />
      <FlotoLocationPicker
        v-if="field.inputType === 'location'"
        v-model="item.value"
        as-input
        :disabled="disabled"
      />
      <FlotoDepartmentPicker
        v-if="field.inputType === 'department'"
        v-model="item.value"
        as-input
        :disabled="disabled"
      />
      <CompanyPicker
        v-if="field.inputType === 'company'"
        v-model="item.value"
        as-input
        :disabled="disabled"
      />
      <ProductPicker
        v-if="field.inputType === 'product'"
        v-model="item.value"
        as-input
        :disabled="disabled"
      />
      <VendorPicker
        v-if="field.inputType === 'vendor'"
        v-model="item.value"
        as-input
        :disabled="disabled"
      />
      <ManufacturerPicker
        v-if="field.inputType === 'manufacturer'"
        v-model="item.value"
        as-input
        :disabled="disabled"
      />
      <SoftwareLicenseTypePicker
        v-if="field.inputType === 'softwareLicenseType'"
        v-model="item.value"
        as-input
        :disabled="disabled"
      />
    </FlotoFormItem>
  </div>
  <div v-else>
    <div class="text-ellipsis">
      <FlotoLocationPicker
        v-if="field.inputType === 'location'"
        v-model="item.value"
        placeholder="---"
        as-input
        text-only
        disabled
      />
      <FlotoDepartmentPicker
        v-else-if="field.inputType === 'department'"
        v-model="item.value"
        placeholder="---"
        as-input
        text-only
        disabled
      />
      <CompanyPicker
        v-else-if="field.inputType === 'company'"
        v-model="item.value"
        placeholder="---"
        as-input
        text-only
        disabled
      />
      <ProductPicker
        v-else-if="field.inputType === 'product'"
        :value="item.value"
        placeholder="---"
        as-input
        text-only
        disabled
      />
      <VendorPicker
        v-else-if="field.inputType === 'vendor'"
        :value="item.value"
        placeholder="---"
        as-input
        text-only
        disabled
      />
      <ManufacturerPicker
        v-else-if="field.inputType === 'manufacturer'"
        :key="item.value"
        :value="item.value"
        placeholder="---"
        as-input
        text-only
        disabled
      />
      <SoftwareLicenseTypePicker
        v-else-if="field.inputType === 'softwareLicenseType'"
        :value="item.value"
        placeholder="---"
        as-input
        text-only
        disabled
      />
      <template v-else>
        <span style="white-space: pre-wrap">{{ getValue(field) }}</span>
      </template>
    </div>
  </div>
</template>
<script>
import ProductPicker from '@components/data-picker/product-picker'
import VendorPicker from '@components/data-picker/vendor-picker'
import ManufacturerPicker from '@components/data-picker/manufacturer-picker'
import SoftwareLicenseTypePicker from '@components/data-picker/software-license-type-picker'
import CompanyPicker from '@components/data-picker/company-picker.vue'
import BytesPicker from '@components/data-picker/bytes-picker'
import HertzPicker from '@components/data-picker/hertz-picker'
import TimePicker from '@components/data-picker/time-picker'
import EnumDropdownPicker from '@modules/asset/components/propery-component-list/enum-dropdown-picker'
import { convertValueForDescription } from '@utils/value'
import { attributeFields } from './attribute-field-map'
export default {
  name: 'BaselineAttributeFieldElement',
  components: {
    ProductPicker,
    VendorPicker,
    ManufacturerPicker,
    BytesPicker,
    HertzPicker,
    SoftwareLicenseTypePicker,
    EnumDropdownPicker,
    TimePicker,
    CompanyPicker,
  },
  inject: ['propertyComponentContext'],
  props: {
    // field: { type: Object, default: undefined },
    item: {
      type: Object,
      default() {
        return {}
      },
    },
    column: { type: [Array, Object], required: true },
    disabled: { type: Boolean, default: false },
  },
  computed: {
    field() {
      return {
        ...(attributeFields().fields.find(
          (f) => f.paramName === this.item.key
        ) || {}),
        name: (this.column || {}).text,
      }
    },
  },
  methods: {
    validationRules(field) {
      let rules = {
        required: true,
      }
      if (field.inputType === 'text') {
        rules.short_text = true
      }
      if (field.inputType === 'number') {
        rules.numeric = true
        rules.min_value = 0
      }
      if (field.rules) {
        rules = {
          ...rules,
          ...field.rules,
        }
      }
      return rules
    },
    getValue(field) {
      if (field.inputType === 'number') {
        return this.item.value
          ? `${this.item.value} ${field.unit ? this.$tc(field.unit) : ''}`
          : '---'
      }
      if (field.inputType === 'dropdown') {
        return this.item.value && field.contextOptionKey
          ? (
              this.propertyComponentContext[field.contextOptionKey].find(
                (i) => i.key === this.item.value
              ) || {}
            ).text
          : '---'
      }
      // if (field.inputType === 'dropdown') {
      //   return this.item.value
      //     ? this.$tc(`asset_enum.${this.item.value}`)
      //     : '---'
      // }
      const value = convertValueForDescription(
        field.inputType,
        this.item.value,
        field
      )
      if (value) {
        return value
      }
      return '---'
    },
  },
}
</script>
<style lang="less" scoped>
.input-group-container {
  top: 3px !important;
  display: flex !important;
  .@{ant-prefix}-input-number {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
}
</style>
<style lang="less">
.input-group-container {
  .duration-type {
    .@{ant-prefix}-select-selection {
      @apply bg-neutral-lightest text-neutral;
    }
  }
}
</style>
